import { MAX_PERSONEN } from "../shared/personen.ts";
import { mehr, MEHR_START, SCHNELLWAHL, weniger, zeigtZaehler } from "./lib/personenwahl.ts";

/**
 * Wie viele Personen ein Bildelement zeigt — die Eingabe dazu (Schritt 5).
 *
 * **Seit dem 08.10.2026 eine Schnellwahl „1 · 2 · 3 · mehr" ohne Startwert** (Annas Mail,
 * Begründung in `src/lib/personenwahl.ts`). Bis dahin stand hier ein Zähler mit −/+ auf 1 —
 * den übersah jeder. Der Zähler ist geblieben, für alles ab 4: „mehr" klappt ihn auf, und
 * wer darin unter 4 geht, steht wieder in der Schnellwahl.
 *
 * **Die Frage steht immer über der Eingabe, nie daneben.** Schnellwahl und Zähler sind
 * damit gleich hoch, und der Wechsel zwischen ihnen lässt die Zeichenfläche in
 * `src/Umfahren.tsx` nicht springen. Kostet hochkant 28 px Zeichenfläche (576 statt 604 px
 * bei 390 × 844, gemessen am Nachbau, 08.10.2026).
 *
 * **Knöpfe und keine Tastatur.** Der Normalfall ist 1, der zweithäufigste 2 oder 3;
 * ein Zahlenfeld holte für jeden dieser Fälle die Bildschirmtastatur hoch und legte sie
 * über das Foto, auf dem gerade gezählt wird. Für ein Gruppenbild mit zwölf Personen ist
 * Tippen mühsam — dafür trifft man die Knöpfe im Stehen, und das ist der Fall, den Annas
 * Kundschaft hat.
 *
 * Steht in einer eigenen Datei, weil dieselbe Eingabe an zwei Stellen vorkommt: beim
 * Umfahren, wo das Foto groß vor einem liegt (ARCHITEKTUR.md 6.2, „grob drumherum ziehen
 * → Personenzahl angeben"), und später im Bildschirm „Bild bearbeiten" als Korrektur.
 *
 * **Vor der Kundin steht „Personen oder Tiere"** (Carolin, 24.09.2026) — das ist Annas
 * eigene Formulierung auf ihrer Produktseite, und ihre Mail nennt einen Hund als
 * schwierigen Fall. Das Feld heißt intern weiter `personen`: `SPEC.md`, AB § 1.3 und der
 * Preis rechnen ausdrücklich **je Person**.
 *
 * ⚠️ Deshalb zeigt die Anzeige zwischen den Knöpfen nur noch die **Zahl**. „4 Personen
 * oder Tiere" neben der Frage passte auf 390 px nicht mehr in eine Zeile, und eine
 * umbrechende Zeile kostet genau die Zeichenfläche, um die es hier geht. Was gezählt wird,
 * sagt die Frage darüber — und für Bildschirmleser steht es unsichtbar dabei.
 */

interface Props {
  /** `null`: noch nicht gewählt. Dann ist kein Knopf hervorgehoben. */
  wert: number | null;
  onAendern: (wert: number) => void;
  /** Die Frage darüber. */
  frage: string;
  /** Schnellwahl zwei mal zwei statt in einer Reihe. Für die schmale Spalte im Querformat:
   *  vier 44-px-Knöpfe passen in deren rund 200 px nur ohne jede Reserve. */
  stapeln?: boolean;
  /**
   * Die Frage hervorheben — im Umfahren, sobald der Umriss steht (Annas Test,
   * 29.09.2026: sie hat zuerst gar nicht gefunden, dass man hier etwas angeben kann).
   *
   * ⚠️ **Nur Farbe und Gewicht, kein Kasten und kein Rand.** Alles, was die Höhe dieser
   * Zeile ändert, ändert die Zeichenfläche darüber — und zwar in genau dem Augenblick,
   * in dem der frisch gezogene Umriss erscheint. Das Bild spränge unter dem Finger weg
   * (siehe Dateikopf von `src/Umfahren.tsx`).
   */
  betont?: boolean;
}

/** Wie über eine Anzahl gesprochen wird — für Bildschirmleser und jeden Fließtext. */
export function personenText(anzahl: number): string {
  return anzahl === 1 ? "1 Person oder Tier" : `${String(anzahl)} Personen oder Tiere`;
}

const KNOPF =
  "min-h-11 min-w-11 rounded-xl border text-lg font-medium disabled:border-neutral-700 disabled:text-neutral-600";
const AUS = "border-neutral-400 text-neutral-100";
const AN = "border-white bg-white text-neutral-900";

export default function Personenzahl({
  wert,
  onAendern,
  frage,
  stapeln = false,
  betont = false,
}: Props) {
  return (
    <div className="flex flex-col gap-2">
      <span
        id="personenzahl-frage"
        className={betont ? "text-sm font-medium text-amber-300" : "text-sm text-neutral-200"}
      >
        {frage}
      </span>
      {wert !== null && zeigtZaehler(wert) ? (
        <div
          className="flex items-center justify-between gap-2"
          role="group"
          aria-labelledby="personenzahl-frage"
        >
          <button
            type="button"
            onClick={() => {
              onAendern(weniger(wert));
            }}
            aria-label="Einer weniger"
            className={`${KNOPF} flex-1 ${AUS}`}
          >
            −
          </button>
          {/* `tabular-nums`, damit die Breite beim Zählen nicht springt und der Plus-Knopf
              unter dem Finger bleibt. Der unsichtbare Zusatz sagt einem Bildschirmleser,
              wovon die Zahl spricht — sehend steht es in der Frage darüber. */}
          <span
            aria-live="polite"
            className="min-w-12 text-center text-lg font-medium text-neutral-100 tabular-nums"
          >
            {String(wert)}
            <span className="sr-only"> {personenText(wert)}</span>
          </span>
          <button
            type="button"
            onClick={() => {
              onAendern(mehr(wert));
            }}
            disabled={wert >= MAX_PERSONEN}
            aria-label="Einer mehr"
            className={`${KNOPF} flex-1 ${AUS}`}
          >
            +
          </button>
        </div>
      ) : (
        <div
          className={`grid gap-2 ${stapeln ? "grid-cols-2" : "grid-cols-4"}`}
          role="group"
          aria-labelledby="personenzahl-frage"
        >
          {SCHNELLWAHL.map((zahl) => (
            <button
              key={zahl}
              type="button"
              aria-pressed={wert === zahl}
              aria-label={personenText(zahl)}
              onClick={() => {
                onAendern(zahl);
              }}
              className={`${KNOPF} ${wert === zahl ? AN : AUS}`}
            >
              {String(zahl)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              onAendern(MEHR_START);
            }}
            aria-label={`Mehr als ${String(MEHR_START - 1)}`}
            className={`${KNOPF} ${AUS}`}
          >
            mehr
          </button>
        </div>
      )}
    </div>
  );
}

/** Die eine Frage, an beiden Stellen dieselbe. */
export const FRAGE_PERSONEN = "Wie viele Personen oder Tiere?";
